import { Editor } from '@tiptap/core';
import { StreamAPI } from 'api/stream';
import {
  getNodePosition,
  getStreamContentInsertPosition,
  scrollToLastNode,
  type NodePosition,
} from 'helpers/stream/stream-node-helpers';

interface StreamToNodeParams {
  identifierType?: 'templateSectionId' | 'documentSectionId' | 'tempId';
  identifierValue?: string;
  shouldFilterByPromptId?: boolean;
}

interface StreamTipTapNodesParams {
  editor: Editor;
  identifierType?: 'templateSectionId' | 'documentSectionId';
  identifierValues?: string[];
  shouldFilterByPromptId?: boolean;
  token?: string;
  channelName: string;
  onStart?: () => void;
  onPostStream?: () => void;
  resolve?: boolean;
}

const createNewNode = (
  editor: Editor,
  nodePosition: NodePosition,
  content: any[],
) => {
  return editor.schema.node(
    nodePosition.node.type,
    {
      ...nodePosition.node.attrs,
      editable: true,
      isTableStatic: false,
    },
    editor.schema.nodeFromJSON({
      type: 'doc',
      content,
    }).content,
  );
};

export const streamTipTapNodes = async ({
  editor,
  identifierType = 'templateSectionId',
  identifierValues = [],
  shouldFilterByPromptId = false,
  token,
  channelName,
  onStart,
  onPostStream,
  resolve = false,
}: StreamTipTapNodesParams) => {
  const streamToNode = async ({
    identifierType,
    identifierValue,
    shouldFilterByPromptId = false,
  }: StreamToNodeParams = {}) => {
    const nodePos = getNodePosition(
      editor,
      identifierType as 'templateSectionId' | 'documentSectionId',
      identifierValue?.toString() ?? '-1',
    );

    if (!nodePos) {
      return;
    }

    const insertPos = getStreamContentInsertPosition(editor, nodePos, resolve);

    if (insertPos === null) {
      return Promise.resolve();
    }

    return new Promise((resolvePromise) => {
      editor.commands?.streamContent(insertPos, async ({ write }) => {
        await StreamAPI.streamFinalContentForMeetingPage({
          token,
          channelName,
          identifierValue: shouldFilterByPromptId ? identifierValue : '',
          onStart,
          onPostProcessing: async (content) => {
            const nodePosition = getNodePosition(
              editor,
              identifierType as 'templateSectionId' | 'documentSectionId',
              identifierValue?.toString() ?? '-1',
            );

            if (!nodePosition) {
              return;
            }

            const contentWithEmptyParagraph = [
              {
                type: 'paragraph',
                content: [],
              },
              ...content,
            ];

            const newNode = createNewNode(
              editor,
              nodePosition,
              contentWithEmptyParagraph,
            );

            editor.commands.command(({ tr, dispatch }) => {
              tr.replaceWith(
                nodePosition.pos,
                nodePosition.pos + nodePosition.node.nodeSize,
                newNode,
              );
              if (dispatch) {
                dispatch(tr);
              }

              return true;
            });
          },
          onContentReceived: async (content) => {
            const contentToWrite =
              typeof content === 'string' ? content : String(content);

            write({ partial: contentToWrite });

            const nodePosition = getNodePosition(
              editor,
              identifierType as 'templateSectionId' | 'documentSectionId',
              identifierValue?.toString() ?? '-1',
            );

            if (nodePosition?.node.lastChild) {
              scrollToLastNode(nodePosition.node.lastChild);
            }
          },
          onStreamingComplete: () => {
            resolvePromise(true);
          },
        });
      });
    });
  };

  if (identifierValues.length === 0) {
    await streamToNode({
      identifierType,
      identifierValue: '-1',
    });
  } else {
    for (const sectionId of identifierValues) {
      await streamToNode({
        identifierType,
        identifierValue: sectionId,
        shouldFilterByPromptId,
      });
    }
  }

  await onPostStream?.();
};
